// OpenAI Service for AI-powered features
import type { AudioFeatures } from './audio-analysis.service';

const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;

export interface ArtistPrediction {
  artist: string;
  confidence: number;
  style: string;
  similarArtists: string[];
  reasoning: string;
}

export interface AudioAnalysisResult {
  bpm: number;
  key: string;
  confidence: number;
  genre?: string;
  mood?: string;
  reasoning: string;
}

class OpenAIService {
  private apiKey: string | null = null;

  constructor() {
    this.apiKey = OPENAI_API_KEY || null;
    
    if (!this.apiKey) {
      console.warn('⚠️ OpenAI API key not configured. AI features will be disabled.');
    } else {
      console.log('✅ OpenAI Service initialized');
    }
  }

  /**
   * Analyze audio file with OpenAI - listens to the actual audio
   * Only sends first 30 seconds to save costs (90% cheaper!)
   */
  async analyzeAudioFile(file: File): Promise<AudioAnalysisResult> {
    if (!this.apiKey) {
      throw new Error('OpenAI API key not configured');
    }

    console.log('🎵 Sending audio to OpenAI for analysis:', file.name);

    try {
      // Trim audio to first 30 seconds to save costs
      const trimmedFile = await this.trimAudioFile(file, 30);
      console.log('✂️ Trimmed audio to 30s to reduce costs');
      
      // Convert file to base64
      const arrayBuffer = await trimmedFile.arrayBuffer();
      const base64Audio = this.arrayBufferToBase64(arrayBuffer);
      
      // Detect format from file extension
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'mp3';
      const audioFormat = ['mp3', 'wav', 'flac', 'm4a', 'ogg'].includes(fileExtension) 
        ? fileExtension 
        : 'mp3';

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-4o-audio-preview',
          modalities: ['text', 'audio'],
          audio: { voice: 'alloy', format: 'wav' },
          messages: [
            {
              role: 'system',
              content: 'You are an expert music producer and audio engineer specializing in analyzing beats, instrumentals, and music tracks. You can accurately detect BPM, musical key, genre, and mood from audio.',
            },
            {
              role: 'user',
              content: [
                {
                  type: 'text',
                  text: `Please analyze this audio track and provide detailed information in JSON format:

**Required Analysis:**
1. BPM (Beats Per Minute) - must be accurate
2. Musical Key (e.g., "C#m", "F", "Gm", "A") - detect the predominant key
3. Confidence level (0-100) - how confident you are in the detection
4. Genre (optional) - what style/genre does this sound like?
5. Mood (optional) - emotional tone (e.g., "dark", "uplifting", "aggressive")
6. Reasoning - briefly explain how you determined BPM and key

**Output Format (JSON only):**
{
  "bpm": 140,
  "key": "C#m",
  "confidence": 95,
  "genre": "Trap",
  "mood": "Dark",
  "reasoning": "Detected regular 808 kicks at 140 BPM with C# minor melodic patterns"
}

Please listen carefully and provide accurate BPM and key detection.`,
                },
                {
                  type: 'input_audio',
                  input_audio: {
                    data: base64Audio,
                    format: audioFormat,
                  },
                },
              ],
            },
          ],
          temperature: 0.3, // Lower temperature for more consistent results
          max_tokens: 500,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ OpenAI API error:', errorData);
        throw new Error(`OpenAI API error: ${response.statusText}`);
      }

      const data = await response.json();
      const content = data.choices[0].message.content;
      
      // Extract JSON from response (might be wrapped in markdown)
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('Failed to parse OpenAI response');
      }

      const result = JSON.parse(jsonMatch[0]);

      console.log('✅ OpenAI audio analysis complete:', result);

      return {
        bpm: result.bpm || 120,
        key: result.key || 'C',
        confidence: result.confidence || 50,
        genre: result.genre,
        mood: result.mood,
        reasoning: result.reasoning || 'Analysis completed',
      };
    } catch (error) {
      console.error('❌ OpenAI audio analysis failed:', error);
      throw error;
    }
  }

  /**
   * Trim audio file to specified duration (to save API costs)
   */
  private async trimAudioFile(file: File, maxDurationSeconds: number): Promise<File> {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const arrayBuffer = await file.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      // If file is shorter than max duration, return as-is
      if (audioBuffer.duration <= maxDurationSeconds) {
        return file;
      }

      // Trim to max duration
      const sampleRate = audioBuffer.sampleRate;
      const numberOfChannels = audioBuffer.numberOfChannels;
      const maxSamples = sampleRate * maxDurationSeconds;
      
      const trimmedBuffer = audioContext.createBuffer(
        numberOfChannels,
        maxSamples,
        sampleRate
      );

      for (let channel = 0; channel < numberOfChannels; channel++) {
        const channelData = audioBuffer.getChannelData(channel);
        const trimmedData = trimmedBuffer.getChannelData(channel);
        trimmedData.set(channelData.slice(0, maxSamples));
      }

      // Convert to WAV blob
      const wavBlob = this.audioBufferToWav(trimmedBuffer);
      return new File([wavBlob], file.name, { type: 'audio/wav' });
    } catch (error) {
      console.warn('⚠️ Failed to trim audio, using original:', error);
      return file;
    }
  }

  /**
   * Convert AudioBuffer to WAV format
   */
  private audioBufferToWav(buffer: AudioBuffer): Blob {
    const numberOfChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;

    const bytesPerSample = bitDepth / 8;
    const blockAlign = numberOfChannels * bytesPerSample;

    const data = this.interleave(buffer);
    const dataLength = data.length * bytesPerSample;
    const headerLength = 44;
    const totalLength = headerLength + dataLength;

    const arrayBuffer = new ArrayBuffer(totalLength);
    const view = new DataView(arrayBuffer);

    // Write WAV header
    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, totalLength - 8, true);
    this.writeString(view, 8, 'WAVE');
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // fmt chunk size
    view.setUint16(20, format, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    this.writeString(view, 36, 'data');
    view.setUint32(40, dataLength, true);

    // Write audio data
    this.floatTo16BitPCM(view, 44, data);

    return new Blob([arrayBuffer], { type: 'audio/wav' });
  }

  /**
   * Interleave channels
   */
  private interleave(buffer: AudioBuffer): Float32Array {
    const numberOfChannels = buffer.numberOfChannels;
    const length = buffer.length;
    const result = new Float32Array(length * numberOfChannels);

    let offset = 0;
    for (let i = 0; i < length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        result[offset++] = buffer.getChannelData(channel)[i];
      }
    }

    return result;
  }

  /**
   * Write string to DataView
   */
  private writeString(view: DataView, offset: number, string: string): void {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  /**
   * Convert float samples to 16-bit PCM
   */
  private floatTo16BitPCM(view: DataView, offset: number, input: Float32Array): void {
    for (let i = 0; i < input.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, input[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
  }

  /**
   * Convert ArrayBuffer to base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Predict matching artist based on audio features
   */
  async predictArtist(
    audioFeatures: AudioFeatures,
    fileName: string
  ): Promise<ArtistPrediction> {
    if (!this.apiKey) {
      throw new Error('OpenAI API key not configured');
    }

    console.log('🤖 Predicting artist for:', fileName);
    console.log('📊 Audio features:', audioFeatures);

    const prompt = this.buildArtistPredictionPrompt(audioFeatures, fileName);

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-4',
          messages: [
            {
              role: 'system',
              content: 'You are an expert music producer and A&R who specializes in identifying artist styles and matching beats to artists. You have deep knowledge of hip-hop, trap, R&B, and pop music production.',
            },
            {
              role: 'user',
              content: prompt,
            },
          ],
          temperature: 0.7,
          max_tokens: 500,
          response_format: { type: 'json_object' },
        }),
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.statusText}`);
      }

      const data = await response.json();
      const result = JSON.parse(data.choices[0].message.content);

      console.log('✅ Artist prediction:', result);

      return {
        artist: result.artist || 'Unknown',
        confidence: result.confidence || 0.5,
        style: result.style || '',
        similarArtists: result.similar_artists || [],
        reasoning: result.reasoning || '',
      };
    } catch (error) {
      console.error('❌ Artist prediction failed:', error);
      throw error;
    }
  }

  /**
   * Build prompt for artist prediction
   */
  private buildArtistPredictionPrompt(
    features: AudioFeatures,
    fileName: string
  ): string {
    return `
Analyze this beat and predict which artist or artist style it would best fit.

**Beat Information:**
- File name: ${fileName}
- BPM: ${features.bpm}
- Key: ${features.key}
- Energy: ${(features.energy * 100).toFixed(0)}%
- Danceability: ${(features.danceability * 100).toFixed(0)}%
- Duration: ${features.duration.toFixed(1)}s

**Task:**
1. Predict the most suitable artist or artist type for this beat
2. Identify the production style/vibe
3. List 3-5 similar artists who would also fit this beat
4. Explain your reasoning based on the audio features

**Output Format (JSON):**
{
  "artist": "Artist Name or Type (e.g., 'Travis Scott', 'Drill Artist', 'Melodic Rapper')",
  "confidence": 0.8,
  "style": "Brief style description (e.g., 'Dark Trap', 'Melodic R&B', 'Drill')",
  "similar_artists": ["Artist 1", "Artist 2", "Artist 3"],
  "reasoning": "Why this beat fits this artist/style"
}

Consider:
- BPM range typical for genres (Trap: 140-160, Drill: 140-150, R&B: 60-90, etc.)
- Energy level (high energy = aggressive rap, low energy = melodic/R&B)
- Key tonality (minor = dark/emotional, major = upbeat/happy)
`;
  }

  /**
   * Generate tags based on audio features
   */
  async generateTags(audioFeatures: AudioFeatures): Promise<string[]> {
    if (!this.apiKey) {
      return [];
    }

    const prompt = `
Based on these audio features, suggest 5-8 relevant tags for this beat:

BPM: ${audioFeatures.bpm}
Key: ${audioFeatures.key}
Energy: ${(audioFeatures.energy * 100).toFixed(0)}%
Danceability: ${(audioFeatures.danceability * 100).toFixed(0)}%

Return ONLY a JSON array of tags, e.g.: ["Dark", "Trap", "808", "Aggressive", "Club"]
`;

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            {
              role: 'system',
              content: 'You are a music tagging expert. Return only JSON arrays of relevant music tags.',
            },
            {
              role: 'user',
              content: prompt,
            },
          ],
          temperature: 0.5,
          max_tokens: 100,
        }),
      });

      const data = await response.json();
      const tags = JSON.parse(data.choices[0].message.content);

      return Array.isArray(tags) ? tags : [];
    } catch (error) {
      console.error('❌ Tag generation failed:', error);
      return [];
    }
  }

  /**
   * Check if OpenAI is configured
   */
  isConfigured(): boolean {
    return this.apiKey !== null;
  }
}

export const openAIService = new OpenAIService();
